import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AiIcon,
  Close as CloseIcon,
  DeleteSweep as LoeschenIcon,
  Send as SendIcon,
  LinkOff as BezugLoesenIcon,
  TableRows as BezugIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '@/store';
import { setAiChatOpen } from '@/store/slices/uiSlice';
import {
  useAskRecordsMutation,
  type AnalysisAnswer,
  type AskAnswer,
  type AskContext,
  type ZeilenAntwort,
} from '@/store/api/searchApi';
import { useKiFunktion } from '@/hooks/useKiFunktion';
import { AnalysisTable } from './AnalysisTable';

/**
 * Der Co-Auditor — ein Chat, der nichts schreibt.
 *
 * Er beantwortet Fragen auf zwei Wegen, und die beiden sehen absichtlich
 * verschieden aus:
 *
 *   • **gerechnet** — eine benannte Auswertung, von der Datenbank gezogen.
 *     Kopf mit Namen und Parametern, Tabelle, Vermerk darunter. Für Fragen
 *     nach dem, was fehlt: Eine Suche antwortet aus Treffern, und Abwesenheit
 *     erzeugt keine.
 *   • **formuliert** — eine Antwort aus den Treffern der Suche, jede Aussage
 *     mit Verweis, Mengenangaben mit Warnung.
 *   • **nachgefragt** — eine Antwort auf die Zeilen der Auswertung, die gerade
 *     dasteht. „Und wer ist dafür zuständig?" ist ohne die vorige Tabelle
 *     keine beantwortbare Frage; darum merkt sich das Fenster, welche
 *     Auswertung angezeigt wird, und gibt sie mit der nächsten Frage mit —
 *     als Schlüssel und Parameter, nie als Zeilen. Gerechnet wird auch dann
 *     in der Datenbank.
 *
 * Dass beides im selben Fenster erscheint, ist der Sinn der Sache; dass man es
 * auseinanderhalten kann, die Bedingung. Darum der Kopf über der Tabelle und
 * der Vermerk darunter — nicht als Zierde, sondern damit ein Missverständnis
 * auffällt, statt in einem Absatz zu verschwinden.
 *
 * Geschrieben wird nichts. Kein Feld, kein Status, keine Einstufung.
 */

interface Nachricht {
  id: number;
  von: 'person' | 'assistent';
  text?: string;
  antwort?: AskAnswer;
  fehler?: string;
}

const VORSCHLAEGE = [
  'Zu welchen Feststellungen fehlt der Nachweis?',
  'Welche Feststellungen haben keine Maßnahme?',
  'Was ist überfällig?',
  'Was hat der Prüfer im Vorjahr festgestellt?',
];

/**
 * Woran die nächste Frage anknüpft.
 *
 * `key` und die Parameter gehen zum Dienst — der rechnet die Auswertung noch
 * einmal, mit den Rechten der fragenden Person. Name, Parametertext und
 * Zeilenzahl stehen nur hier, für die Zeile über dem Eingabefeld: Der Bezug
 * muss sichtbar sein, sonst antwortet das Fenster irgendwann auf eine Tabelle,
 * an die niemand mehr denkt.
 */
interface Bezug extends AskContext {
  name: string;
  parameterText: string;
  rowCount: number;
}

const ZIEL: Record<string, (id: string) => string> = {
  finding: (id) => `/findings/${id}`,
  measure: (id) => `/measures/${id}`,
};

/** Die formulierte Antwort: Text mit Verweisen, Treffer darunter. */
const Bestandsantwort: React.FC<{ a: Exclude<AskAnswer, AnalysisAnswer>; onSprung: (pfad: string) => void }> = ({
  a,
  onSprung,
}) => {
  // Eine Anschlussfrage steht auf anderen Füßen als eine Suche: Die Grundlage
  // sind die Zeilen einer gerechneten Tabelle, nicht Treffer. Das muss
  // dastehen — sonst sieht beides gleich aus und ist es nicht.
  const zeilen = a.mode === 'rows' ? (a as ZeilenAntwort) : null;
  return (
  <Box>
    {a.answer.split('\n').map((z, i) =>
      z.trim() ? (
        <Typography key={i} variant="body2" sx={{ mb: 0.75 }}>
          {z}
        </Typography>
      ) : null,
    )}

    {a.countClaims?.length > 0 && (
      <Alert severity="warning" sx={{ mt: 1 }}>
        Der Text nennt eine Menge: {a.countClaims.join(', ')} — gezählt hat das Modell, und das zählt nicht
        zuverlässig. Maßgeblich ist die Liste der Treffer.
      </Alert>
    )}
    {a.unknownRefs?.length > 0 && (
      <Alert severity="error" sx={{ mt: 1 }}>
        Verweise ohne Treffer: {a.unknownRefs.join(', ')} — bitte prüfen.
      </Alert>
    )}

    {a.records?.length > 0 && (
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          {zeilen ? 'Die Zeilen' : 'Die Treffer'} ({a.hitsConsidered} von {a.hitsTotal})
        </Typography>
        <Stack spacing={0.25}>
          {a.records.map((t) => {
            const pfad = ZIEL[t.type]?.(t.id);
            return (
              <Typography
                key={t.key}
                variant="caption"
                onClick={pfad ? () => onSprung(pfad) : undefined}
                sx={{
                  color: pfad ? 'primary.main' : 'text.secondary',
                  cursor: pfad ? 'pointer' : 'default',
                  '&:hover': pfad ? { textDecoration: 'underline' } : undefined,
                }}
              >
                {t.key} · {t.title}
              </Typography>
            );
          })}
        </Stack>
      </Box>
    )}

    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
      {zeilen
        ? `Formuliert aus den Zeilen von „${zeilen.basis.name}" (${zeilen.basis.parameterText}) — gerechnet hat die Abfrage.`
        : a.mode === 'count'
          ? 'Gezählt hat die Suche, nicht das Modell.'
          : 'Formuliert aus den Treffern — maßgeblich ist die Liste, nicht der Absatz.'}
    </Typography>
  </Box>
  );
};

export const AiChatPanel: React.FC = () => {
  const offen = useAppSelector((s) => s.ui.aiChatOpen);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const ki = useKiFunktion('record_question');
  const [fragen, { isLoading }] = useAskRecordsMutation();

  const [verlauf, setVerlauf] = useState<Nachricht[]>([]);
  const [bezug, setBezug] = useState<Bezug | null>(null);
  const [eingabe, setEingabe] = useState('');
  const ende = useRef<HTMLDivElement>(null);
  const zaehler = useRef(0);

  // Ans Ende rollen, sobald etwas dazukommt — wie in jedem Chat.
  useEffect(() => {
    // Optionaler Aufruf: Nicht jede Umgebung kennt scrollIntoView, und ein
    // fehlendes Rollen darf das Fenster nicht zerlegen.
    ende.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [verlauf, isLoading]);

  const schliessen = () => dispatch(setAiChatOpen(false));

  const sprungZu = (pfad: string) => {
    navigate(pfad);
    schliessen();
  };

  const senden = async (frage: string) => {
    const text = frage.trim();
    if (!text || isLoading) return;
    setEingabe('');
    zaehler.current += 1;
    setVerlauf((v) => [...v, { id: zaehler.current, von: 'person', text }]);
    try {
      // Der Bezug geht als Schlüssel und Parametern mit, nie als Zeilen: Der
      // Dienst rechnet die Auswertung noch einmal und prüft dabei die Rechte.
      // Eine Antwort auf Zeilen, die der Browser mitgeschickt hat, wäre keine
      // Antwort aus dem Bestand.
      const antwort = await fragen({
        question: text,
        ...(bezug
          ? { context: { key: bezug.key, engagement: bezug.engagement, year: bezug.year, text: bezug.text } }
          : {}),
      }).unwrap();
      zaehler.current += 1;
      setVerlauf((v) => [...v, { id: zaehler.current, von: 'assistent', antwort }]);

      if (antwort.mode === 'analysis') {
        // Die neue Tabelle ist ab jetzt der Bezug — auch wenn es dieselbe
        // Auswertung mit engeren Parametern ist.
        setBezug({
          key: antwort.key,
          engagement: antwort.parameters?.engagement,
          year: antwort.parameters?.year,
          text: antwort.parameters?.text,
          name: antwort.name,
          parameterText: antwort.parameterText,
          rowCount: antwort.rowCount,
        });
      } else if (antwort.mode !== 'rows') {
        // Die Frage ging an die Suche, nicht an die Tabelle. Dann ist das
        // Gespräch weitergezogen, und ein stehengebliebener Bezug führte die
        // nächste Frage in die Irre.
        setBezug(null);
      }
    } catch (e) {
      const m = (e as { data?: { message?: string | string[] } })?.data?.message;
      zaehler.current += 1;
      setVerlauf((v) => [
        ...v,
        {
          id: zaehler.current,
          von: 'assistent',
          fehler: Array.isArray(m) ? m.join('; ') : m || 'Die Frage konnte nicht beantwortet werden.',
        },
      ]);
    }
  };

  if (!ki.verfuegbar) return null;

  return (
    <Drawer
      anchor="right"
      open={offen}
      onClose={schliessen}
      // Breiter als ein gewöhnliches Chat-Fenster: Hier stehen Tabellen, und
      // eine abgeschnittene Statusspalte ist keine Antwort. Die überfälligen
      // Maßnahmen bringen fünf Spalten mit — auf einem großen Bildschirm ist
      // der Platz da, also wird er genommen.
      PaperProps={{ sx: { width: { xs: '100%', sm: 560, lg: 680 }, display: 'flex', flexDirection: 'column' } }}
    >
      {/*
        Platzhalter in Höhe des Kopfbalkens. Der Drawer beginnt am oberen
        Bildrand, der Balken liegt darüber — ohne diesen Abstand verschwindet
        die Kopfzeile des Fensters dahinter, mitsamt Titel, Löschen und
        Schließen. Sie war da und nur nicht erreichbar.
      */}
      <Toolbar />
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: 'ki.main', width: 34, height: 34 }}>
          <AiIcon fontSize="small" />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
            Co-Auditor
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Fragt den Bestand ab — schreibt nichts
          </Typography>
        </Box>
        {/*
          Ein Gesprächsverlauf, den man nicht wegräumen kann, wird mit jeder
          Frage unübersichtlicher. Der Knopf erscheint erst, wenn es etwas zu
          löschen gibt — und löscht nur die Anzeige: Gespeichert war ohnehin
          nichts, die Aufrufe stehen im Änderungsnachweis.
        */}
        {verlauf.length > 0 && (
          <Tooltip title="Verlauf löschen">
            <IconButton
              onClick={() => {
                setVerlauf([]);
                setBezug(null);
              }}
              size="small"
              aria-label="Verlauf löschen"
            >
              <LoeschenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Schließen">
          <IconButton onClick={schliessen} size="small" aria-label="Chat schließen">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
        {/*
          Ohne Vorlauftext: Wer das Fenster öffnet, will fragen, nicht lesen.
          Die Vorschläge bleiben — sie sind der Weg zu den Funktionen, und
          siebzehn, die niemand findet, sind so gut wie keine.
        */}
        {verlauf.length === 0 && (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {VORSCHLAEGE.map((v) => (
              <Chip
                key={v}
                label={v}
                size="small"
                variant="outlined"
                onClick={() => senden(v)}
                sx={{ borderColor: 'ki.main', color: 'ki.main' }}
              />
            ))}
          </Stack>
        )}

        <Stack spacing={2}>
          {verlauf.map((n) =>
            n.von === 'person' ? (
              <Box key={n.id} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Paper
                  elevation={0}
                  sx={{ px: 1.5, py: 1, maxWidth: '85%', bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2 }}
                >
                  <Typography variant="body2">{n.text}</Typography>
                </Paper>
              </Box>
            ) : (
              <Box key={n.id} sx={{ display: 'flex', gap: 1 }}>
                <Avatar sx={{ bgcolor: 'ki.main', width: 26, height: 26, mt: 0.25 }}>
                  <AiIcon sx={{ fontSize: 15 }} />
                </Avatar>
                <Paper variant="outlined" sx={{ px: 1.5, py: 1.25, flex: 1, minWidth: 0, borderRadius: 2 }}>
                  {n.fehler ? (
                    <Alert severity="error" sx={{ py: 0 }}>
                      {n.fehler}
                    </Alert>
                  ) : n.antwort?.mode === 'analysis' ? (
                    <AnalysisTable a={n.antwort} onSprung={sprungZu} />
                  ) : n.antwort ? (
                    <Bestandsantwort a={n.antwort} onSprung={sprungZu} />
                  ) : null}
                </Paper>
              </Box>
            ),
          )}

          {isLoading && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'ki.main', width: 26, height: 26 }}>
                <AiIcon sx={{ fontSize: 15 }} />
              </Avatar>
              <Typography variant="caption" color="text.secondary" aria-live="polite">
                sucht im Bestand …
              </Typography>
            </Box>
          )}
        </Stack>
        <div ref={ende} />
      </Box>

      <Divider />
      {/*
        Der Bezug steht sichtbar über dem Feld. Ein Kontext, den man nicht
        sieht, ist die unangenehmste Sorte: Die Antwort bezöge sich auf eine
        Tabelle, an die niemand mehr denkt. Darum steht hier, worauf sich die
        nächste Frage bezieht — und daneben der Knopf, der ihn löst.
      */}
      {bezug && (
        <Box
          sx={{
            px: 1.5,
            pt: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            color: 'text.secondary',
          }}
        >
          <BezugIcon sx={{ fontSize: 15 }} />
          <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap>
            Bezug: {bezug.name} · {bezug.parameterText} · {bezug.rowCount}{' '}
            {bezug.rowCount === 1 ? 'Zeile' : 'Zeilen'}
          </Typography>
          <Tooltip title="Bezug lösen">
            <IconButton onClick={() => setBezug(null)} size="small" aria-label="Bezug lösen">
              <BezugLoesenIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder={bezug ? 'Nachfragen oder neue Frage …' : 'Frage stellen …'}
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void senden(eingabe);
            }
          }}
          inputProps={{ 'aria-label': 'Frage an den Co-Auditor' }}
        />
        <Tooltip title="Senden">
          <span>
            <IconButton
              color="primary"
              onClick={() => void senden(eingabe)}
              disabled={!eingabe.trim() || isLoading}
              aria-label="Frage senden"
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Drawer>
  );
};
